import { useMemo } from 'react'

import { useQueries } from '@tanstack/react-query'
import { AgChartOptions } from 'ag-charts-community'

import { getTMinus, getTMinus1, toISO8601 } from '@fortem-monorepo/js-utilities'
import { AgGridWidget } from '@fortem-monorepo/react-ag'
import { CustomAgChart } from '@fortem-monorepo/react-ag'
import { FiltersProvider, useApi, useFilters } from '@fortem-monorepo/react-spa'
import { createFilters } from '@fortem-monorepo/react-spa'
import { HeaderConfigBar } from '@fortem-monorepo/react-spa'
import { Widget, WidgetContent, WidgetHeaderWithQuery } from '@fortem-monorepo/react-spa'
import { DateRangePicker } from '@fortem-monorepo/react-ui'
import { SearchSelect } from '@fortem-monorepo/react-ui'
import { FormItem } from '@fortem-monorepo/react-ui'
import { Label } from '@fortem-monorepo/react-ui'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@fortem-monorepo/react-ui'
import { Switch } from '@fortem-monorepo/react-ui'
import { Attributions } from '@fortem-monorepo/web-sdk'

import { Page } from '@/components/Page'
import { PM } from '@/constants/links'
import {
  cumulativeColDefs,
  nonCumulativeColDefs,
} from '@/pages/pm/attributions/model-comparison/col-defs'
import { useFunds } from '@/react-query/queries/funds/funds'

const filters = createFilters([
  {
    type: 'daterange',
    name: 'dates',
    label: 'Dates',
    params: true,
    defaultValue: { st_dt: getTMinus(7), ed_dt: getTMinus1() },
  },
  {
    type: 'select',
    name: 'fund',
    label: 'Fund',
    params: true,
    defaultValue: 'FCPGF',
  },
  {
    type: 'boolean',
    name: 'cumulative',
    label: 'Cumulative',
    params: true,
    defaultValue: false,
  },
])

export interface ModelComparisonPageProps {}

export const ModelComparisonPage: React.FC<ModelComparisonPageProps> = ({}) => {
  return (
    <Page
      metadata={{ title: 'Fund Attributions' }}
      permissions={PM.ATTRIBUTIONS.FUND_ATTRIBUTIONS.permissions}
    >
      <FiltersProvider filters={filters}>
        <ModelComparison />
      </FiltersProvider>
    </Page>
  )
}

export interface ModelComparisonProps {}

export const ModelComparison: React.FC<ModelComparisonProps> = ({}) => {
  const { values, registerFilter } = useFilters<typeof filters>()

  const api = useApi()

  const { data: funds, query: fundsQuery } = useFunds()

  const chartParams: Attributions.GetModelComparisonChartParams = {
    st_dt: values.dates?.st_dt ? toISO8601(values.dates.st_dt!) : '',
    ed_dt: values.dates?.ed_dt ? toISO8601(values.dates.ed_dt!) : '',
    fund_name: values.fund!,
  }

  const dataParams: Attributions.GetModelComparisonDataParams = {
    ...chartParams,
    cumulative: values.cumulative!,
  }

  const queries = useQueries({
    queries: [
      {
        queryKey: ['attributions', 'model-comparison', 'data', dataParams],
        queryFn: () => api.attributions.modelComparison.getData(dataParams),
        enabled: !!dataParams.st_dt && !!dataParams.ed_dt && !!dataParams.fund_name,
      },
      {
        queryKey: ['attributions', 'model-comparison', 'chart', chartParams],
        queryFn: () => api.attributions.modelComparison.getChart(chartParams),
        enabled: !!chartParams.st_dt && !!chartParams.ed_dt && !!chartParams.fund_name,
      },
    ],
  })

  const [dataQuery, chartQuery] = queries

  const chartData = useMemo(() => {
    return (chartQuery.data ?? []).map((item) => ({
      ...item,
      value_date: new Date(item.value_date),
    }))
  }, [chartQuery.data])

  const chartOptions: AgChartOptions<(typeof chartData)[number]> = {
    legend: {
      item: {
        marker: {
          shape: 'square',
          strokeWidth: 0,
        },
      },
    },
    title: {
      text: `Fund Performance - ${values.fund === 'all' ? 'FCPGF' : values.fund}`,
      fontSize: 18,
      spacing: 25,
    },
    navigator: {
      enabled: true,
      height: 20,
    },
    data: chartData,
    series: [
      {
        type: 'line',
        xKey: 'value_date',
        yKey: 'bbg_perf_cum_pct',
        yName: 'BBG Performance',
      },
      {
        type: 'line',
        xKey: 'value_date',
        yKey: 'fc_perf_cum_pct',
        yName: 'Rec Performance',
      },
    ],
    axes: [
      {
        type: 'ordinal-time',
        position: 'bottom',
        parentLevel: {
          enabled: true,
        },
      },
      {
        type: 'number',
        position: 'left',
        label: { format: '#{.2f}%' },
      },
    ],
  }

  return (
    <>
      <HeaderConfigBar>
        <div className="flex items-center gap-2">
          <FormItem>
            <Label className={'text-muted-foreground text-xs'}>Dates</Label>
            <DateRangePicker size={'sm'} {...registerFilter('dates')} />
          </FormItem>
          <FormItem>
            <Label className={'text-muted-foreground text-xs'}>Fund</Label>
            <SearchSelect
              size={'sm'}
              {...registerFilter('fund')}
              loading={fundsQuery.isLoading}
              options={[
                // { label: "Show All", value: "all" },
                ...funds.map((fund) => ({ label: fund.fund_name, value: fund.fund_name })),
              ]}
            />
          </FormItem>
          <FormItem>
            <Label className={'text-muted-foreground text-xs'}>Cumulative?</Label>
            <Switch {...registerFilter('cumulative')} />
          </FormItem>
        </div>
      </HeaderConfigBar>
      <ResizablePanelGroup direction={'vertical'}>
        <ResizablePanel>
          <AgGridWidget
            query={dataQuery}
            widget={{ exposed: true, className: 'h-full' }}
            widgetHeader={{
              title: values.cumulative
                ? `Ops Cumulative Attributions - ${values.fund}`
                : `Ops Attributions Time Series - ${values.fund}`,
            }}
            grid={{
              domLayout: 'normal',
              rowData: dataQuery.data?.data ?? [],
              columnDefs: values.cumulative ? cumulativeColDefs : nonCumulativeColDefs,
            }}
          />
        </ResizablePanel>
        <ResizableHandle />
        <ResizablePanel>
          <Widget exposed className="h-full">
            <WidgetHeaderWithQuery
              title={'Nav Change from BBG vs Ops Attributions'}
              query={chartQuery}
            />
            <WidgetContent loading={chartQuery.isLoading}>
              <CustomAgChart className={'h-full'} options={chartOptions} />
            </WidgetContent>
          </Widget>
        </ResizablePanel>
      </ResizablePanelGroup>
    </>
  )
}
